import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';
import { ChatMessage } from 'model/chatbot';
import { ChatbotGetChatHistory, ChatbotGetNewMessages, ChatbotSendMessage } from 'utils/api-client';
import {
  Button,
  Box,
  Drawer,
  ModalProps,
  TextField,
} from '@mui/material';
import { PermissionsTypes } from 'model/role-permissions';
import { userHasPermissionOnResource } from 'utils/user-permissions';
import { useAuthContext } from 'contexts/auth-context';

interface Props {
  /* Callback to execute when the user closes the chatbot */
  onClose?: ModalProps['onClose'];
  /* Whether the chatbot is open */
  open: boolean;
}

const Chatbot = ({ open, onClose = () => { } }: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAuthContext();

  // Refs to scroll to the bottom of the chat
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Scroll to the bottom of the chat when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Scroll to the bottom each time the drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 0);
    }
  }, [open]);

  // Fetches and updates the chat history for the current user
  async function UpdateChatHistory() {
    ChatbotGetChatHistory()
      .then((data) => {
        setMessages(data);
      })
      .catch((err) => {
        console.error('Failed to load chat history', err);
      });
  }

  const pollForResponses = useCallback(async () => {
    try {
      // Fetch new messages from the server
      const newMessages = await ChatbotGetNewMessages();
      if (typeof newMessages !== 'string' && newMessages.length > 0) {
        // Filter out messages that are already in the chat history
        // Use functional update to access current messages without depending on it
        setMessages((prev) => {
          const existingMessageTimestamps = new Set(prev.map((msg) => msg.timestamp));
          const filteredMessages = newMessages.filter((msg) => !existingMessageTimestamps.has(msg.timestamp));
          return [...prev, ...filteredMessages];
        });
      }
    } finally {
      // Ensure polling continues even if an error occurs
      pollInterval.current = setTimeout(() => {
        pollForResponses();
      }, 3000);
    }
  }, []);

  // Load chat history on mount
  useEffect(() => {
    // Check if the user has permission to read/write in the chatbot
    if (userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'chatbot', user?.ownPermissions, false)) {
      UpdateChatHistory().then(() => {
        // Start polling for new messages after loading initial chat history
        pollForResponses();
      });
    }

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [user, pollForResponses]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const newMsg = {
      email: '<currentUser>', role: 'user', content: input, timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, newMsg]);
    setInput('');

    try {
      await ChatbotSendMessage(newMsg);
    } catch (err) {
      console.error('Send failed', err);
    }
  };

  // Hide the component if the user does not have ReadWrite permission
  if (!userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'chatbot', user?.ownPermissions, false)) {
    return null;
  }

  const content = (
    <Box
      px={3}
      py={2}
      width={400}
      height="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Scrollable messages area */}
      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        mb={2} // spacing from the input
      >
        {messages.map((msg) => (
          <Box
            key={msg.timestamp}
            display="flex"
            justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
          >
            <Box
              sx={{
                backgroundColor: msg.role === 'user' ? '#e3f2fd' : '#f3e5f5',
                color: msg.role === 'user' ? '#1565c0' : '#6a1b9a',
                borderRadius: 3,
                p: 1,
                m: 0.5,
                maxWidth: '80%',
                wordBreak: 'break-word',
                boxShadow: 1,
                alignItems: 'center',
                ...(msg.role === 'user'
                  ? { borderTopRightRadius: 0 }
                  : { borderTopLeftRadius: 0 }),
              }}
            >
              <span>{msg.content}</span>
            </Box>
          </Box>
        ))}
        {/* Dummy div for autoscroll */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area always visible */}
      <Box display="flex" justifyContent="flex-end">
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Type your message..."
          fullWidth
          sx={{ maxWidth: '90%', mr: 1 }}
        />
        <Button onClick={sendMessage}>Send</Button>
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

export default Chatbot;
